import { faFileLines, faFilePdf } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { skipToken } from "@reduxjs/toolkit/query";
import axios from "axios";
import parse from "html-react-parser";
import FileDownload from "js-file-download";
import moment from "moment";
import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Stack,
  Accordion,
  Button,
  Breadcrumb,
} from "react-bootstrap";
import { useParams, Link } from "react-router-dom";

import "../Products.css";
import { useGetMaintenanceByMaintIdQuery } from "api/public/project/maintenance/maintenance.api";
import Subheader from "components/Subheader";
import { useLoading } from "components/UI/LoadingSpinner/UseLoading";
import { useInterval } from "hooks/useInterval";
import amFileIcon from "images/assurance-maintenance-file-icon.svg";

export default function CheckAssuranceMaintenancePermDetails() {
  const { product_id, id } = useParams();
  const [hasPerm, setHasPerm] = useState();

  const { _data, error } = useGetMaintenanceByMaintIdQuery(id || skipToken);

  useEffect(() => {
    // If there's a 401 error, always set hasPerm to false regardless of other conditions
    if (error && error.status === 401) {
      setHasPerm(false);
      return;
    }
    // Only check PCL product status if we have a product_id and there's no 401 error
    if (product_id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/is_pcl_product/?product_id=${product_id}`,
        )
        .then((response) => {
          if (response.data?.is_pcl_product === false) {
            setHasPerm(false);
          } else {
            setHasPerm(true);
          }
        })
        .catch(() => {
          setHasPerm(false);
        });
    } else if (!product_id) {
      // If no product_id, set hasPerm to false
      setHasPerm(false);
    }
  }, [product_id, error]);

  if (hasPerm) {
    return <AssuranceMaintenancePage />;
  } else if (hasPerm === false) {
    return (
      <div>
        Assurance Maintenance does not exist or is not viewable on the PCL.
      </div>
    );
  }
  return <div></div>;
}

function AssuranceMaintenancePage() {
  const { product_id, id } = useParams();
  const [maintenance, setMaintenance] = useState({});
  const [project, setProject] = useState({});
  const [projpps, setProjpps] = useState([]);
  const [files, setFiles] = useState([]);
  const [validationReportNumber, setValidationReportNumber] = useState("");
  const { showLoadingSpinner, hideLoadingSpinner } = useLoading();

  useEffect(() => {
    document.title = "NIAP - Assurance Maintenance Details";
  }, []);

  const loadProtectionProductId = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/protection-profiles/get_all_projpp_by_product_id/?product_id=${product_id}`,
      )
      .then((response) => {
        setProjpps(response.data);
      })
      .catch((error) => {
        setProjpps([]);
      });
  }, [product_id]);

  useEffect(() => {
    loadProtectionProductId();
  }, [loadProtectionProductId]);

  useInterval(() => {
    loadProtectionProductId();
  }, 180000); //3 min

  const fetchProduct = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/product/get_product_by_product_id/?product_id=${product_id}`,
      )
      .then((response) => {
        setProject(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [product_id]);

  useEffect(() => {
    fetchProduct();
  }, [fetchProduct]);

  useInterval(() => {
    fetchProduct();
  }, 60000); //1 min

  const fetchMaintenance = useCallback(() => {
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}project/maintenance/get_frontend_maintenance_by_maintenance_id/?maintenance_id=${id}`,
      )
      .then((response) => {
        setMaintenance(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }, [id]);

  useEffect(() => {
    fetchMaintenance();
  }, [fetchMaintenance]);

  useInterval(() => {
    fetchMaintenance();
  }, 60000); //1 min

  const pullFiles = useCallback(() => {
    if (project?.product_id) {
      axios
        .get(
          `${import.meta.env.VITE_DJANGO_ENDPOINT}file/get_pcl_files/?product_id=${project?.product_id}`,
        )
        .then((response) => {
          setFiles(response.data);
          let validationReport = response.data?.eval_files?.find(
            (file) => file?.file_display_name === "Validation Report",
          );
          if (validationReport) {
            setValidationReportNumber(
              `CCEVS-VR-VID${product_id}-${new Date(validationReport?.uploaded_on).getFullYear()}`,
            );
          }
        })
        .catch((error) => {
          setFiles([]);
        });
    }
  }, [project, maintenance.maintenance_id]);

  useEffect(() => {
    pullFiles();
  }, [project, maintenance.maintenance_id]);

  const downloadFile = (file) => {
    showLoadingSpinner("Downloading");
    axios
      .get(
        `${import.meta.env.VITE_DJANGO_ENDPOINT}file/get_public_file/?file_id=${file.file_id}`,
        {
          responseType: "blob",
        },
      )
      .then((response) => {
        hideLoadingSpinner();
        FileDownload(
          response.data,
          file.file_new_name ? file.file_new_name : file.file_name,
        );
      })
      .catch((error) => {
        console.log(error);
      });
  };

  return (
    <>
      <Subheader pageName={"Products"} />
      <Container className="p-5" fluid>
        <Row>
          <Breadcrumb>
            <Breadcrumb.Item href={`/products/${product_id}`}>
              VID {project?.v_id} Details
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{maintenance?.title}</Breadcrumb.Item>
          </Breadcrumb>
        </Row>
        <Row>
          <Col className="d-flex my-4">
            <h3 className="fw-bold text-primary">
              ASSURANCE CONTINUITY - {maintenance?.title}
            </h3>
          </Col>
        </Row>
        <Row className="gx-2">
          <Col sm={12} md={3} lg={2} className="bg-primary p-4 text-white">
            {maintenance?.completion_date && (
              <Stack className="border-bottom pt-4">
                <small className="pb-2">Maintenance Completion Date</small>
                <p>
                  {moment
                    .utc(maintenance?.completion_date)
                    .format("MM/DD/YYYY")}
                </p>
              </Stack>
            )}
            <Stack className="border-bottom pt-4">
              <small className="pb-2">Product Type</small>
              <p>{project?.tech_type}</p>
            </Stack>
            <Stack className="border-bottom pt-4">
              <small className="pb-2">Conformance Claim</small>
              {projpps?.map((projpp) => (
                <div
                  key={projpp?.projpp_id}
                  className="d-flex align-items-center py-2 pb-3"
                >
                  <FontAwesomeIcon
                    icon={faFileLines}
                    color="var(--bs-yellow)"
                    className="me-2"
                    size="lg"
                  />
                  <Link
                    to={`/protectionprofiles/${projpp?.pp__pp_id}`}
                    className="text-white"
                  >
                    <p className="py-0 my-0">{projpp?.pp__pp_name}</p>
                  </Link>
                </div>
              ))}
            </Stack>
            <Stack className="border-bottom pt-4">
              <small className="pb-2">Original Evaluated TOE</small>
              <Link
                to={`/products/${product_id}`}
                className="text-white mb-3"
                target="_blank"
              >
                {project?.certification_date
                  ? moment.utc(project.certification_date).format("YYYY-MM-DD")
                  : "N/A"}{" "}
                - {project?.product_name}
              </Link>
              {/* we only want to show CC Certificate, validation report, assurance activity, and administrative guide here */}
              {files?.eval_files
                ?.filter((eval_file) =>
                  [
                    "CC Certificate",
                    "Validation Report",
                    "Assurance Activity Report (AAR)",
                    "Administrative Guide (AGD)",
                  ].includes(eval_file?.file_display_name),
                )
                ?.map((eval_file, idx) => (
                  <Row className="mt-3" key={idx}>
                    <Stack direction="horizontal">
                      <FontAwesomeIcon
                        icon={faFilePdf}
                        color="red"
                        size="xl"
                        className="px-1"
                      />
                      <Button
                        variant="link"
                        onClick={() => downloadFile(eval_file)}
                        className="text-white d-flex justify-content-start p-0 m-0 small-font"
                        style={{ textAlign: "left" }}
                      >
                        {eval_file?.file_display_name}
                      </Button>
                    </Stack>
                  </Row>
                ))}
              <p className="mt-3">
                Please note: The above files are for the Original Evaluated TOE.
                Consequently, they do not refer to this maintained version,
                although they apply to the maintained version.{" "}
              </p>
            </Stack>
            <Stack className="border-bottom pb-4">
              <Accordion className="border-0" defaultActiveKey={"1"}>
                <Accordion.Item eventKey="1">
                  <Accordion.Header className="custom-accordion-header">
                    <small className="p-0 text-white">Documents</small>
                  </Accordion.Header>
                  <Accordion.Body className="p-0 mt-3 text-white">
                    {files?.am_files?.map((file) => (
                      <Row className="mt-3" key={file?.file_id}>
                        <Stack direction="horizontal">
                          <img
                            src={amFileIcon}
                            alt="am-file-icon"
                            className="me-2"
                          />
                          <Button
                            variant="link"
                            onClick={() => downloadFile(file)}
                            className="text-white d-flex justify-content-start p-0 m-0 small-font"
                            style={{
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          >
                            <p className="m-0">
                              {file?.file_label}
                              {file?.file_display_name === "Security Target" &&
                                "*"}
                            </p>
                          </Button>
                        </Stack>
                      </Row>
                    ))}
                    <p className="mt-3">
                      Please note: This serves as an addendum to the VR for the
                      Original Evaluated TOE.
                    </p>
                    <p className="mt-3">
                      * This is the Security Target (ST) associated with this
                      latest Maintenance Release.
                    </p>
                  </Accordion.Body>
                </Accordion.Item>
              </Accordion>
            </Stack>
          </Col>
          <Col sm={12} md={9} lg={10} className="px-4">
            <Accordion className="mb-4" flush>
              <Accordion.Item>
                <Accordion.Header>
                  <h6 className="text-primary fw-bold">
                    MAINTENANCE DESCRIPTION
                  </h6>
                </Accordion.Header>
                <Accordion.Body>
                  {maintenance?.maintenance_description &&
                    parse(maintenance?.maintenance_description)}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            <Accordion className="my-4" flush>
              <Accordion.Item>
                <Accordion.Header>
                  <h6 className="text-primary fw-bold">SUMMARY OF CHANGES</h6>
                </Accordion.Header>
                <Accordion.Body>
                  {maintenance?.change_summary &&
                    parse(maintenance?.change_summary)}
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
            <Row className="mt-2 mb-2">
              <Col>
                <Stack>
                  <h6 className="fw-bold">VENDOR INFORMATION</h6>
                  <p>{project?.vendor_id?.name}</p>
                  <p>{project?.vendor_poc}</p>
                  <p>{project?.vendor_phone}</p>
                  <p>{project?.vendor_email}</p>
                  <a
                    href={
                      project?.vendor_id?.website?.includes("https://")
                        ? project?.vendor_id?.website
                        : "https://" + project?.vendor_id?.website
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    {project?.vendor_id?.website}
                  </a>
                </Stack>
              </Col>
            </Row>
          </Col>
          <div className="mt-5">
            <p>
              <span className="fw-bold">
                Readers are reminded that the certification of this product
                (TOE) is the result of maintenance, rather than an actual
                re-evaluation of the product.
              </span>
              &nbsp; Maintenance only considers the affect of TOE changes on the
              assurance baseline (i.e. the original evaluated TOE); maintenance
              is not intended to provide assurance in regard to the resistance
              of the TOE to new vulnerabilities or attack methods discovered
              since the date of the initial certificate. Such assurance can only
              be gained through re-evaluation.
            </p>
            <p>
              Using a security impact analysis of the changes made to the TOE,
              which was provided by the developer, the CCEVS has determined that
              the impact of changes on the TOE are considered minor and that
              independent evaluator analysis was not necessary. A summary of the
              results can be found in the Maintenance Report, which is written
              in relation to the product's original validation report and
              Security Target.
              <span className="fw-bold">
                Readers are therefore reminded to read the Security Target,
                Validation Report, and the Assurance Maintenance Report to fully
                understand the meaning of what a maintained certificate
                represents.
              </span>
            </p>
          </div>
        </Row>
      </Container>
    </>
  );
}
